import { type CSSProperties, type KeyboardEvent, type MouseEvent } from 'react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

import { getClickableToastHandlerForTarget } from '@apps/web/lib/clickable_toasts';
import { useTheme } from '@apps/web/lib/theme_store';

function clickedToastControl(target: EventTarget | null): boolean {
    return (
        target instanceof Element &&
        target.closest('[data-close-button], [data-button]') !== null
    );
}

function Toaster({ ...props }: ToasterProps) {
    const theme = useTheme();
    const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
        if (clickedToastControl(event.target)) {
            return;
        }

        const onClick = getClickableToastHandlerForTarget(event.target);
        if (onClick === null) {
            return;
        }

        onClick();
    };

    const handleKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        if (clickedToastControl(event.target)) {
            return;
        }

        const onClick = getClickableToastHandlerForTarget(event.target);
        if (onClick === null) {
            return;
        }

        event.preventDefault();
        onClick();
    };

    return (
        <div
            onClickCapture={handleClickCapture}
            onKeyDownCapture={handleKeyDownCapture}
        >
            <Sonner
                className="toaster group"
                theme={theme}
                offset={{
                    right: 'calc(var(--app-toast-right-offset, 0px) + 24px)',
                }}
                style={
                    {
                        '--width':
                            'min(356px, max(0px, calc(var(--app-toast-page-width, 100vw) - 48px)))',
                        '--normal-bg': 'var(--popover)',
                        '--normal-text': 'var(--popover-foreground)',
                        '--normal-border': 'var(--border)',
                    } as CSSProperties
                }
                {...props}
            />
        </div>
    );
}

export { Toaster };
